import { Component, Inject, OnInit } from '@angular/core';
import { ProjectElement } from '../../../../resources/element/project-element.interface';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { UserService } from '../../../../resources/user/user.service';
import { ConstantsService } from '../../../../utilities/constants/constants.service';
import { ConstantsStatusType } from '../../../../utilities/constants/constants.interface';
import { ElementAssignToOptions } from './assign-to.interface';
import { ElementLog } from '../../../../utilities/stores/element-log/element-log.interface';
import { User } from '../../../../resources/user/user.interface';

/**
 * Quick Response modal for elements.
 */
@Component({
  selector: 'sg2-assign-to',
  templateUrl: './assign-to.component.html',
  styleUrls: ['./assign-to.component.less']
})
export class ModalAssignToComponent implements OnInit {

  /** Current element. */
  public element: ProjectElement;

  /** Form. */
  public form: FormGroup;

  /** Preset used in form. */
  public preset: ElementAssignToOptions;

  /** Constants status list. */
  public statuses: ConstantsStatusType[];


  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly config: any,
    private constantsService: ConstantsService,
    public dialogRef: MatDialogRef<ModalAssignToComponent>,
    private formBuilder: FormBuilder,
    public userService: UserService
  ) {
    this.element = config.element;
    this.statuses = this.constantsService.getStatusTypes();
    this._setPreset(config.preset);
    this._setForm();
  }

  ngOnInit() {
    this._trackUser();
  }

  /**
   * Submit.
   */
  public submit() {
    this.dialogRef.close({
      user: this.form.controls.user.value,
      status: this.form.controls.status.value,
      ... this._sanitizeComment(this.form.controls.comment.value)
    } as ElementAssignToOptions);
  }

  /**
   * Generate or modify comment.
   *
   * @param userId The id of the user to tag as prefix.
   * @returns The generated or modified comment.
   */
  private _getComment(userId: User['_id']): ElementLog['text'] {
    let comment = this.form && this.form.controls.comment.value || '';
    if (comment.charAt(0) === '@') {
      comment = comment.split(' ').slice(1).join(' ');
    }
    const user = this.userService.getUser(userId);
    const prefix = user
      ? `@${ this.userService.getUser(userId).nameFirst } `
      : '';
    return prefix + comment;
  }

  /**
   * Make sure empty comments are not submitted.
   *
   * @note A comment consisting of only `@username` is also considered empty.
   *
   * @param comment The comment to check before submit.
   * @returns Comment when eligible.
   */
  private _sanitizeComment(comment): { comment: ElementLog['text'] } | { } {
    return comment.trimEnd().split(' ').length > 1
      ? { comment: comment }
      : { };
  }

  /**
   * Initialize form based on preset.
   */
  private _setForm(): void {
    this.form = this.formBuilder.group({
      user: this.preset.user,
      status: this.preset.status,
      comment: this._getComment(this.preset.user)
    });
  }

  /**
   * Set preset.
   */
  private _setPreset(config: ElementAssignToOptions): void {
    this.preset = {
      user: config.user || this.element.assignedToUserId,
      status: config.status || this.element.status,
      comment: ''
    };
  }

  /**
   * Track user input field.
   *
   * Update comment when setting/changing user.
   */
  private _trackUser(): void {
    this.form.controls.user.valueChanges.subscribe(user => {
      this.form.controls.comment.setValue(this._getComment(user));
    });
  }
}
